import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import { entityClient } from '../services/entityClient';
import { Plus, Edit2, Trash2, X, Briefcase, Activity, TrendingUp, TrendingDown, LayoutDashboard, FileText } from 'lucide-react';
import { useFocusEntity } from '../hooks/useFocusEntity';

const Projects = () => {
  const { focusedId, registerRow } = useFocusEntity();
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  // Phase 7.x · Fix #8 — Cost Engine modal context.
  const [costEngineCtx, setCostEngineCtx] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    client_name: '',
    customer_id: '',
    contract_id: '',
    description: '',
    budget: '',
    start_date: '',
    end_date: '',
    status: 'planning'
  });

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => {
      fetchData();
    }, 60000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, customersRes, contractsRes] = await Promise.all([
        axiosInstance.get('/projects'),
        axiosInstance.get('/customers'),
        axiosInstance.get('/contracts')
      ]);
      setProjects(projectsRes.data);
      setCustomers(customersRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      if (loading) {
        toast.error('Failed to fetch data');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProject) {
        await axiosInstance.put(`/projects/${editingProject.id}`, formData);
        toast.success('Project updated successfully');
      } else {
        const response = await axiosInstance.post('/projects', formData);
        if (response.data.approval_status === 'pending') {
          toast.success('Project submitted for approval', {
            description: 'Waiting for admin approval before it appears in the list'
          });
        } else {
          toast.success('Project created successfully');
        }
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (project) => {
    const id = typeof project === 'string' ? project : project.id;
    const displayName = typeof project === 'object' ? (project.name || project.id) : undefined;
    const res = await entityClient.delete('project', id, { displayName });
    if (res.mode === 'deleted') { toast.success('تم حذف المشروع'); fetchData(); }
    else if (res.mode === 'archived') { fetchData(); }
    else if (res.mode === 'error') { toast.error('فشل الحذف: ' + (res.error?.response?.data?.detail || res.error?.message || '')); }
  };

  // Phase 7.x · Fix #8 — Cost Engine modal. Fetches the P&L snapshot
  // from /api/projects/{id}/cost-engine and renders the 4 buckets +
  // gross profit + margin. Period filtering can be wired later.
  const openCostEngine = async (project) => {
    setCostEngineCtx({ project, data: null });
    try {
      const r = await axiosInstance.get(`/projects/${project.id}/cost-engine`);
      setCostEngineCtx({ project, data: r.data });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load cost engine');
      setCostEngineCtx(null);
    }
  };

  const handleEdit = (project) => {
    setEditingProject(project);
    setFormData({
      name: project.name,
      client_name: project.client_name,
      customer_id: project.customer_id || '',
      contract_id: project.contract_id || '',
      description: project.description || '',
      budget: project.budget,
      start_date: project.start_date,
      end_date: project.end_date || '',
      status: project.status
    });
    setShowModal(true);
  };

  // Get contracts for selected customer
  const customerContracts = contracts.filter(c => c.customer_id === formData.customer_id);

  // Handle customer change - also update client_name and reset contract
  const handleCustomerChange = (customerId) => {
    const customer = customers.find(c => c.id === customerId);
    setFormData({
      ...formData,
      customer_id: customerId,
      client_name: customer ? (customer.name_english || customer.name_arabic) : formData.client_name,
      contract_id: ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      client_name: '',
      customer_id: '',
      contract_id: '',
      description: '',
      budget: '',
      start_date: '',
      end_date: '',
      status: 'planning'
    });
    setEditingProject(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  if (loading) {
    return <div className="text-center py-12">Loading projects...</div>;
  }

  return (
    <div data-testid="projects-page">
      <div className="page-header">
        <h2 className="page-title">Projects</h2>
        <button
          data-testid="add-project-button"
          onClick={() => setShowModal(true)}
          className="btn-primary"
        >
          <Plus className="w-5 h-5 inline mr-2" />
          New Project
        </button>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Project Name</th>
              <th>Client</th>
              <th>Contract</th>
              <th>Budget</th>
              <th>Spent</th>
              <th>Start Date</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {projects.length > 0 ? (
              projects.map((project) => (
                <tr
                  key={project.id}
                  ref={registerRow(project.id)}
                  data-testid={`project-row-${project.id}`}
                  className={focusedId === project.id ? 'focus-highlight' : ''}
                >
                  <td className="font-semibold">{project.name}</td>
                  <td>{project.client_name}</td>
                  <td>
                    {project.contract_number ? (
                      <span className="text-purple-600 font-medium">{project.contract_number}</span>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td>{project.budget.toLocaleString()} SAR</td>
                  <td>{(project.spent || 0).toLocaleString()} SAR</td>
                  <td>{new Date(project.start_date).toLocaleDateString()}</td>
                  <td>
                    <span className={`status-badge ${project.status}`}>
                      {project.status.replace('_', ' ')}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link
                        data-testid={`control-hub-${project.id}`}
                        to={`/projects/${project.id}/control-hub`}
                        className="btn-icon"
                        title="Project Control Hub · Budget vs Committed vs Actual"
                        style={{ background: '#fef3c7', color: '#92400e' }}
                      >
                        <LayoutDashboard className="w-4 h-4" />
                      </Link>
                      <Link
                        data-testid={`pece-open-${project.id}`}
                        to={`/projects/${project.id}/pece`}
                        className="btn-icon"
                        title="فتح PECE Workspace"
                        style={{ background: '#eef2ff', color: '#4338ca' }}
                      >
                        <Briefcase className="w-4 h-4" />
                      </Link>
                      {/* Phase 7.x · Fix #8 — open Cost Engine snapshot. */}
                      <button
                        type="button"
                        data-testid={`open-cost-engine-${project.id}`}
                        onClick={() => openCostEngine(project)}
                        className="btn-icon"
                        title="Project Cost Engine · P&L"
                        style={{ background: '#ecfdf5', color: '#065f46' }}
                      >
                        <Activity className="w-4 h-4" />
                      </button>
                      {/* Statement of account — opens Project Statement directly. */}
                      <Link
                        data-testid={`project-statement-${project.id}`}
                        to={`/accounting-reports?tab=project-statement&project_id=${project.id}`}
                        className="btn-icon"
                        title="كشف حساب المشروع"
                        style={{ background: '#eff6ff', color: '#1d4ed8' }}
                      >
                        <FileText className="w-4 h-4" />
                      </Link>
                      <button
                        data-testid={`edit-project-${project.id}`}
                        onClick={() => handleEdit(project)}
                        className="btn-icon edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        data-testid={`delete-project-${project.id}`}
                        onClick={() => handleDelete(project)}
                        className="btn-icon delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="8" className="text-center py-12 text-gray-500">
                  No projects found. Create your first project!
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingProject ? 'Edit Project' : 'New Project'}
              </h3>
              <button
                data-testid="close-modal-button"
                onClick={handleCloseModal}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Project Name</label>
                <input
                  data-testid="project-name-input"
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Customer</label>
                  <select
                    value={formData.customer_id}
                    onChange={(e) => handleCustomerChange(e.target.value)}
                    className="form-input"
                  >
                    <option value="">Select Customer (Optional)</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.name_english || c.name_arabic}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Contract</label>
                  <select
                    value={formData.contract_id}
                    onChange={(e) => setFormData({ ...formData, contract_id: e.target.value })}
                    className="form-input"
                    disabled={!formData.customer_id}
                  >
                    <option value="">Select Contract (Optional)</option>
                    {customerContracts.map(c => (
                      <option key={c.id} value={c.id}>{c.contract_number} - {c.contract_title}</option>
                    ))}
                  </select>
                  {!formData.customer_id && (
                    <p className="text-xs text-gray-500 mt-1">Select a customer first to see contracts</p>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Client Name</label>
                <input
                  data-testid="project-client-input"
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  className="form-input"
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea
                  data-testid="project-description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="form-input"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Budget (SAR)</label>
                <input
                  data-testid="project-budget-input"
                  type="number"
                  value={formData.budget}
                  onChange={(e) => setFormData({ ...formData, budget: e.target.value })}
                  className="form-input"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input
                    data-testid="project-start-date-input"
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    className="form-input"
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input
                    data-testid="project-end-date-input"
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Status</label>
                <select
                  data-testid="project-status-select"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="form-select"
                >
                  <option value="planning">Planning</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  data-testid="submit-project-button"
                  type="submit"
                  className="btn-primary flex-1"
                >
                  {editingProject ? 'Update Project' : 'Create Project'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Phase 7.x · Fix #8 · Project Cost Engine modal.
          Shows the real-time P&L snapshot of a project: revenue (from
          posted invoices) minus three cost buckets (direct expenses /
          asset purchases / payroll allocations). Period scoping can be
          added via a select dropdown once Period Engine UI is mature. */}
      {costEngineCtx && (
        <div
          className="modal-overlay"
          onClick={() => setCostEngineCtx(null)}
        >
          <div
            data-testid="cost-engine-modal"
            className="modal-content max-w-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3 className="modal-title flex items-center gap-2">
                <Activity className="w-5 h-5 text-emerald-600" />
                Project Cost Engine · {costEngineCtx.project.name}
              </h3>
              <button
                data-testid="close-cost-engine-btn"
                onClick={() => setCostEngineCtx(null)}
                className="btn-icon"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-5">
              {!costEngineCtx.data && (
                <p className="text-sm text-gray-500 italic">Loading P&L snapshot…</p>
              )}
              {costEngineCtx.data && costEngineCtx.data.missing && (
                <p className="text-sm text-rose-600">Project not found in scope.</p>
              )}
              {costEngineCtx.data && !costEngineCtx.data.missing && (
                <div className="space-y-4">
                  {/* Header KPIs */}
                  <div className="grid grid-cols-3 gap-3">
                    <div
                      data-testid="ce-revenue"
                      className="p-3 rounded-lg bg-emerald-50 border border-emerald-100"
                    >
                      <p className="text-xs text-emerald-700 font-medium flex items-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" />
                        Revenue
                      </p>
                      <p className="text-xl font-bold text-emerald-900">
                        {(costEngineCtx.data.revenue?.total || 0).toLocaleString()} SAR
                      </p>
                      <p className="text-xs text-emerald-600">
                        {costEngineCtx.data.revenue?.count || 0} invoice(s)
                      </p>
                    </div>
                    <div
                      data-testid="ce-cost"
                      className="p-3 rounded-lg bg-rose-50 border border-rose-100"
                    >
                      <p className="text-xs text-rose-700 font-medium flex items-center gap-1">
                        <TrendingDown className="w-3.5 h-3.5" />
                        Total Cost
                      </p>
                      <p className="text-xl font-bold text-rose-900">
                        {(costEngineCtx.data.total_cost || 0).toLocaleString()} SAR
                      </p>
                      <p className="text-xs text-rose-600">
                        Expenses + Assets + Payroll
                      </p>
                    </div>
                    <div
                      data-testid="ce-gross-profit"
                      className={`p-3 rounded-lg border ${
                        (costEngineCtx.data.gross_profit || 0) >= 0
                          ? 'bg-indigo-50 border-indigo-100'
                          : 'bg-orange-50 border-orange-200'
                      }`}
                    >
                      <p className="text-xs text-indigo-700 font-medium">
                        Gross Profit
                      </p>
                      <p className={`text-xl font-bold ${
                        (costEngineCtx.data.gross_profit || 0) >= 0
                          ? 'text-indigo-900'
                          : 'text-orange-700'
                      }`}>
                        {(costEngineCtx.data.gross_profit || 0).toLocaleString()} SAR
                      </p>
                      <p className="text-xs text-indigo-600">
                        {costEngineCtx.data.margin !== null && costEngineCtx.data.margin !== undefined
                          ? `Margin: ${(costEngineCtx.data.margin * 100).toFixed(1)}%`
                          : 'Margin: N/A'}
                      </p>
                    </div>
                  </div>

                  {/* Cost buckets detail */}
                  <div>
                    <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold mb-2">
                      Cost Breakdown
                    </p>
                    <div className="space-y-2">
                      {[
                        { label: 'Direct Expenses', bucket: costEngineCtx.data.direct_expenses, key: 'direct_expenses' },
                        { label: 'Asset Purchases', bucket: costEngineCtx.data.asset_costs, key: 'asset_costs' },
                        { label: 'Payroll Allocations', bucket: costEngineCtx.data.payroll_costs, key: 'payroll_costs' },
                      ].map(({ label, bucket, key }) => (
                        <div
                          key={key}
                          data-testid={`ce-bucket-${key}`}
                          className="flex items-center justify-between p-2 bg-gray-50 rounded-lg border border-gray-100"
                        >
                          <span className="text-sm text-gray-700">
                            {label}
                            <span className="text-xs text-gray-400 ml-2">
                              · {bucket?.count || 0} row(s)
                            </span>
                          </span>
                          <span className="font-semibold text-gray-900">
                            {(bucket?.total || 0).toLocaleString()} SAR
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {costEngineCtx.data.period && (
                    <p className="text-xs text-gray-500 text-center pt-2 border-t border-gray-100">
                      Period scope: {costEngineCtx.data.period.name} · {costEngineCtx.data.period.start_date} → {costEngineCtx.data.period.end_date}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Projects;